/**
 * DAC customization: Klaviyo featured reviews carousel
 * Product titles + theme Swiper takeover (loop / center / autoplay).
 *
 * Klaviyo boots a cssMode Swiper without Autoplay. Built-in `loop` fails with
 * slidesPerView:'auto', so we use cloned slides + an invisible translate relink
 * (same idea as theme IconBoxesSlider) for continuous forward looping.
 */
import { Swiper, Navigation, Autoplay } from '@theme/swiper';

const ROOT = '#klaviyo-featured-reviews-carousel';
const NAME_CLASS = 'dac-kl-reviews-carousel__product-name';
const AUTOPLAY_MS = 3000;
const SPEED_MS = 500;

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

const enhanceCard = (image) => {
  const wrapper = image.closest('.kl_reviews__carousel__image_wrapper');
  if (!wrapper) return;

  const title = (image.getAttribute('alt') || '').trim();
  if (!title) return;

  let nameEl = wrapper.querySelector(`.${NAME_CLASS}`);
  if (!nameEl) {
    nameEl = document.createElement('span');
    nameEl.className = NAME_CLASS;
    wrapper.appendChild(nameEl);
  }
  if (nameEl.textContent !== title) nameEl.textContent = title;
};

const enhanceRoot = (root) => {
  root.querySelectorAll('.kl_reviews__carousel__image').forEach(enhanceCard);
};

const stripCssMode = (el) => {
  el.classList.remove('swiper-css-mode');
  const wrapper = el.querySelector('.swiper-wrapper');
  if (!wrapper) return;
  wrapper.style.transform = '';
  wrapper.style.transitionDuration = '';
  wrapper.style.scrollBehavior = '';
  wrapper.style.overflow = '';
  wrapper.style.scrollSnapType = '';
  wrapper.scrollLeft = 0;
};

const originalSlides = (swiper) =>
  Array.from(swiper.slidesEl.querySelectorAll(`.${swiper.params.slideClass}`)).filter(
    (slide) => !slide.classList.contains('swiper-slide-duplicate')
  );

const cloneCountForViewport = (swiper, slides) => {
  const slideWidth = slides[0]?.offsetWidth || 400;
  const gap = Number(swiper.params.spaceBetween) || 0;
  const stride = slideWidth + gap;
  const visible = Math.max(2, Math.ceil(swiper.width / stride) + 1);
  // At least one full original set on each side so the edge never goes blank.
  return Math.max(slides.length, visible * 2, 4);
};

const createCloneLoop = (swiper) => {
  const { slidesEl } = swiper;
  slidesEl.querySelectorAll('.swiper-slide-duplicate').forEach((node) => node.remove());

  const slides = originalSlides(swiper);
  if (slides.length < 2) return 0;

  slides.forEach((slide, index) => {
    slide.setAttribute('data-swiper-slide-index', String(index));
  });

  const loopedSlides = cloneCountForViewport(swiper, slides);

  for (let i = 0; i < loopedSlides; i += 1) {
    const source = slides[i % slides.length];
    const clone = source.cloneNode(true);
    clone.classList.add('swiper-slide-duplicate');
    clone.setAttribute('data-swiper-slide-index', source.getAttribute('data-swiper-slide-index'));
    clone.setAttribute('aria-hidden', 'true');
    slidesEl.appendChild(clone);
  }

  for (let i = 0; i < loopedSlides; i += 1) {
    const source = slides[(slides.length - 1 - (i % slides.length) + slides.length) % slides.length];
    const clone = source.cloneNode(true);
    clone.classList.add('swiper-slide-duplicate');
    clone.setAttribute('data-swiper-slide-index', source.getAttribute('data-swiper-slide-index'));
    clone.setAttribute('aria-hidden', 'true');
    slidesEl.prepend(clone);
  }

  swiper.recalcSlides();
  swiper.updateSlides();
  swiper.updateSlidesClasses();
  swiper.allowSlidePrev = true;
  swiper.allowSlideNext = true;
  return loopedSlides;
};

/**
 * Invisible relink: after we slide onto a clone, shift the track by the exact
 * pixel delta to the matching original so the next "Next" keeps flowing forward.
 * Never use slideTo() here — that animates back across the strip (the rewind).
 */
const relinkToOriginal = (swiper) => {
  if (!swiper || swiper.destroyed) return;

  const active = swiper.slides[swiper.activeIndex];
  if (!active?.classList.contains('swiper-slide-duplicate')) {
    swiper.allowSlidePrev = true;
    swiper.allowSlideNext = true;
    return;
  }

  const realIndex = Number(active.getAttribute('data-swiper-slide-index'));
  if (Number.isNaN(realIndex)) return;

  const target = originalSlides(swiper).find(
    (slide) => Number(slide.getAttribute('data-swiper-slide-index')) === realIndex
  );
  if (!target) return;

  const targetIndex = swiper.getSlideIndex(target);
  if (targetIndex < 0 || targetIndex === swiper.activeIndex) return;

  const visualLeft = active.getBoundingClientRect().left;
  const diff = target.offsetLeft - active.offsetLeft;

  const wrapper = swiper.wrapperEl;
  swiper.setTransition(0);
  if (wrapper) wrapper.style.transitionDuration = '0ms';

  if (swiper.touchEventsData) {
    swiper.touchEventsData.startTranslate -= diff;
    swiper.touchEventsData.currentTranslate -= diff;
  }

  swiper.setTranslate(swiper.translate - diff);
  swiper.updateActiveIndex(targetIndex);
  swiper.updateSlidesClasses();

  // Kill sub-pixel drift so the centered card doesn't twitch
  const linked = swiper.slides[targetIndex];
  if (linked) {
    const drift = linked.getBoundingClientRect().left - visualLeft;
    if (Math.abs(drift) > 0.5) {
      swiper.setTranslate(swiper.translate - drift);
    }
  }

  swiper.allowSlidePrev = true;
  swiper.allowSlideNext = true;
  // Leave transition at 0 — the next slideNext()/slidePrev() sets speed again.
};

const bindCloneLoop = (swiper) => {
  // Only relink after a settled slide. Doing it mid-drag causes visible jumps.
  swiper.on('slideChangeTransitionEnd', () => relinkToOriginal(swiper));
  swiper.on('touchEnd', () => {
    // After a flick settles, Swiper may already have fired transitionEnd; call
    // again safely (no-op when not on a duplicate).
    requestAnimationFrame(() => relinkToOriginal(swiper));
  });

  const originalUpdateSlidesClasses = swiper.updateSlidesClasses.bind(swiper);
  swiper.updateSlidesClasses = (...args) => {
    originalUpdateSlidesClasses(...args);
    swiper.allowSlidePrev = true;
    swiper.allowSlideNext = true;
  };

  let resizeTimer = 0;
  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (swiper.destroyed) return;
      const active = swiper.slides[swiper.activeIndex];
      const realIndex = Number(active?.getAttribute('data-swiper-slide-index'));
      createCloneLoop(swiper);
      const originals = originalSlides(swiper);
      const target =
        originals.find((slide) => Number(slide.getAttribute('data-swiper-slide-index')) === realIndex) ||
        originals[0];
      if (target) {
        swiper.setTransition(0);
        swiper.slideTo(swiper.getSlideIndex(target), 0, false);
      }
      enhanceRoot(swiper.el.closest(ROOT) || document);
    }, 150);
  };
  window.addEventListener('resize', onResize);

  swiper.on('destroy', () => {
    window.removeEventListener('resize', onResize);
    window.clearTimeout(resizeTimer);
  });
};

const mountThemeSwiper = (root) => {
  const el = root.querySelector('.swiper');
  if (!el?.swiper || !el.querySelector('.swiper-slide')) return null;
  if (root.dataset.dacKlNativeSwiper === 'true' && el.swiper && !el.classList.contains('swiper-css-mode')) {
    return el.swiper;
  }

  const nextEl = root.querySelector('.kl_reviews__carousel__next_button');
  const prevEl = root.querySelector('.kl_reviews__carousel__prev_button');

  stripCssMode(el);
  el.swiper.destroy(false, false);
  stripCssMode(el);

  const motionOff = reducedMotion();
  const swiper = new Swiper(el, {
    modules: [Navigation, Autoplay],
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 10,
    loop: false,
    cssMode: false,
    speed: motionOff ? 0 : SPEED_MS,
    watchOverflow: false,
    resistanceRatio: 0.85,
    navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
    autoplay: motionOff
      ? false
      : {
          delay: AUTOPLAY_MS,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
  });

  stripCssMode(el);

  const looped = createCloneLoop(swiper);
  if (looped > 0) {
    const firstOriginal = originalSlides(swiper)[0];
    const startIndex = firstOriginal ? swiper.getSlideIndex(firstOriginal) : looped;
    swiper.setTransition(0);
    swiper.slideTo(startIndex, 0, false);
    bindCloneLoop(swiper);
  }

  enhanceRoot(root);
  root.dataset.dacKlNativeSwiper = 'true';
  return swiper;
};

const initRoot = (root) => {
  if (!root || root.dataset.dacKlCarouselInit === 'true') return;
  root.dataset.dacKlCarouselInit = 'true';
  enhanceRoot(root);

  const tryMount = () => Boolean(mountThemeSwiper(root));

  if (!tryMount()) {
    const ready = new MutationObserver(() => {
      if (tryMount()) ready.disconnect();
    });
    ready.observe(root, { childList: true, subtree: true });
  }

  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      enhanceRoot(root);
    });
  }).observe(root, { childList: true, subtree: true });
};

const init = () => {
  const root = document.querySelector(ROOT);
  if (root) {
    initRoot(root);
    return;
  }

  const bodyObserver = new MutationObserver(() => {
    const late = document.querySelector(ROOT);
    if (!late) return;
    bodyObserver.disconnect();
    initRoot(late);
  });
  bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
};

init();
